import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, BookOpen, Users, MessageSquare, FileText, Play, ArrowRight, ChevronRight, Search, GraduationCap, Award, Video } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { API } from '@/App';

const AnimatedCounter = ({ end, suffix = '', visible }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!visible) return;
    let f = 0;
    const steps = 60;
    const timer = setInterval(() => {
      f++;
      const p = f / steps;
      const e = 1 - Math.pow(1 - p, 3);
      setCount(Math.round(e * end));
      if (f >= steps) clearInterval(timer);
    }, 2000 / steps);
    return () => clearInterval(timer);
  }, [visible, end]);
  return <>{count}{suffix}</>;
};

const useReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const HomePage = () => {
  const navigate = useNavigate();
  const [hero, setHero] = useState({});
  const [about, setAbout] = useState({});
  const [ctaCards, setCtaCards] = useState({});
  const [partners, setPartners] = useState({});
  const [recentFormations, setRecentFormations] = useState([]);

  const [aboutRef, aboutVisible] = useReveal(0.15);
  const [ctaRef, ctaVisible] = useReveal(0.1);
  const [partnersRef, partnersVisible] = useReveal(0.15);

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => {
      r.data.forEach(s => {
        if (s.section === 'hero') setHero(s.data || {});
        if (s.section === 'about') setAbout(s.data || {});
        if (s.section === 'cta_cards') setCtaCards(s.data || {});
        if (s.section === 'partners') setPartners(s.data || {});
      });
    }).catch(() => {});
    axios.get(`${API}/formations/public`).then(r => {
      const all = r.data || [];
      const sorted = all.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setRecentFormations(sorted.slice(0, 6));
    }).catch(() => {});
  }, []);

  const defaultParagraphs = [
    "La SFOALC est une association créée dans les années 2000 qui regroupe l'ensemble des ophtalmologistes qui pratiquent la contactologie médicale.",
    "Forte de son ancienneté et de son expérience, la SFOALC a souhaité se mettre à votre service avec ce nouveau site internet afin de faciliter votre pratique de la contactologie médicale.",
    "Vous y trouverez de la formation continue, des fiches pratiques, la localisation des médecins adaptateurs, les informations concernant les congrès à venir...",
    "Vous pourrez également soutenir l'association en cotisant et bénéficier d'un espace personnalisé dédié permettant d'accéder à des formations, des replays, des tutos ainsi qu'à un forum d'échanges.",
  ];
  const paragraphs = about.paragraphs?.length > 0 ? about.paragraphs : defaultParagraphs;

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <SEO title="Accueil" description="SFOALC - Société Française des Ophtalmologistes Adaptateurs de Lentilles de Contact. L'adaptation des lentilles de contact est un acte médical." />
      <SiteHeader />

      {/* ===== HERO — Matches sfo-alc.fr (bleu ciel #35AFE4) ===== */}
      <section className="relative overflow-hidden h-[55vh] sm:h-[58vh] md:h-[62vh]" style={{ minHeight: '420px', maxHeight: '680px' }} data-testid="hero-section">
        {/* Solid sky-blue background like the reference site (no video bleed) */}
        <div className="absolute inset-0">
          <div className="absolute inset-0" style={{ background: '#35AFE4' }} />
          <iframe
            src={`${hero.video_url || 'https://www.youtube.com/embed/ZjRdtQ1i8fo'}?controls=0&rel=0&playsinline=1&autoplay=1&mute=1&loop=1&playlist=ZjRdtQ1i8fo&cc_load_policy=0&showinfo=0&modestbranding=1`}
            className="absolute top-1/2 left-1/2 pointer-events-none"
            style={{ width: '177.78vh', height: '100vh', minWidth: '100vw', minHeight: '56.25vw', transform: 'translate(-50%, -50%)', border: 'none', opacity: 0.04 }}
            allow="autoplay; encrypted-media"
            title="SFOALC"
          />
        </div>

        {/* Hero content — centered, clean like the reference */}
        <div className="relative z-10 flex items-center justify-center h-full pt-14">
          <div className="text-center max-w-4xl mx-auto px-4">
            <p className="text-white text-base sm:text-lg tracking-[0.15em] uppercase mb-4 font-semibold drop-shadow" data-testid="hero-subtitle">
              {hero.subtitle || 'Bienvenue sur le site de la SFOALC'}
            </p>
            <h1 className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-[44px] font-bold mb-5 leading-tight" style={{ fontFamily: "'Barlow', sans-serif", textShadow: '0 2px 16px rgba(4,84,116,0.45)' }} data-testid="hero-title">
              Société Française des Ophtalmologistes{' '}<br className="hidden sm:block" />
              Adaptateurs de Lentilles de Contact
            </h1>
            <p className="text-white text-sm sm:text-base lg:text-lg italic drop-shadow" data-testid="hero-tagline">
              {hero.tagline || "L'adaptation des lentilles de contact est un acte médical"}
            </p>
            <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button onClick={() => navigate('/annuaire')} className="px-7 py-3 bg-white text-[#045474] rounded-full font-bold text-sm hover:shadow-xl hover:scale-105 transition-all flex items-center gap-2" data-testid="hero-annuaire-btn">
                <Search size={16} /> Trouver un praticien
              </button>
              <button onClick={() => navigate('/adhesion')} className="px-7 py-3 bg-[#045474] text-white rounded-full font-bold text-sm hover:bg-[#03415c] hover:shadow-xl transition-all flex items-center gap-2" data-testid="hero-join-btn">
                Rejoindre la SFOALC <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom curve — smaller on mobile to avoid large white gap */}
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" preserveAspectRatio="none" className="block w-full h-[30px] sm:h-[45px] md:h-[60px]">
            <path fill="#ffffff" d="M1000,4.3V0H0v4.3C0.9,23.1,126.7,99.2,500,100S1000,22.7,1000,4.3z" style={{ transform: 'rotate(180deg)', transformOrigin: 'center' }} />
          </svg>
        </div>
      </section>

      {/* ===== QUI SOMMES-NOUS — 2 columns like reference ===== */}
      <section ref={aboutRef} id="about" className="bg-white py-20 sm:py-28 px-4 sm:px-6" data-testid="about-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Text */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-[#35AFE4]" style={{ fontFamily: "'Barlow', sans-serif" }}>
                {about.title || 'Qui sommes-nous ?'}
              </h2>
              <div className="space-y-4 text-gray-600 text-sm sm:text-[15px] leading-[1.85]" style={{ textAlign: 'justify' }}>
                {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                <p className="font-semibold text-[#35AFE4] text-base mt-6">
                  {about.closing || "Alors gardons le contact pour mieux adapter nos patients !"}
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={() => navigate('/annuaire')} className="flex items-center gap-2 px-5 py-2.5 bg-[#35AFE4] text-white rounded-full text-sm font-semibold hover:bg-[#1e9bd4] transition-colors" data-testid="about-annuaire-btn">
                  <MapPin size={16} /> Découvrir l'annuaire
                </button>
                <button onClick={() => navigate('/nos-formations')} className="flex items-center gap-2 px-5 py-2.5 border-2 border-[#35AFE4] text-[#35AFE4] rounded-full text-sm font-semibold hover:bg-[#eaf6fc] transition-colors" data-testid="about-articles-btn">
                  <BookOpen size={16} /> Nos publications
                </button>
              </div>
            </div>

            {/* Logo + Stats — right column like reference */}
            <div className="flex flex-col items-center">
              <img src="/SFOALC-logo.png" alt="SFOALC" className="h-14 mb-8 opacity-90" />
              <h3 className="text-[#35AFE4] font-bold text-xl mb-8 text-center" style={{ fontFamily: "'Barlow', sans-serif" }}>
                L'association en quelques chiffres
              </h3>
              <div className="grid grid-cols-2 gap-8 w-full max-w-sm">
                <div className="text-center" data-testid="counter-regions">
                  <p className="text-sm text-gray-500 mb-2">sont représentées</p>
                  <p className="text-5xl font-bold text-[#35AFE4]" style={{ fontFamily: "'Barlow', sans-serif" }}>
                    <AnimatedCounter end={about.stat_regions || 12} visible={aboutVisible} />
                  </p>
                  <p className="text-lg text-[#35AFE4]/70 font-medium">Régions</p>
                </div>
                <div className="text-center" data-testid="counter-years">
                  <p className="text-sm text-gray-500 mb-2">d'existence</p>
                  <p className="text-5xl font-bold text-[#35AFE4]" style={{ fontFamily: "'Barlow', sans-serif" }}>
                    +<AnimatedCounter end={about.stat_years || 25} visible={aboutVisible} />
                  </p>
                  <p className="text-lg text-[#35AFE4]/70 font-medium">Ans</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== VOUS ÊTES OPHTALMOLOGISTE ? — gradient: white top, blue center/bottom ===== */}
      <section ref={ctaRef} className="relative py-20 sm:py-24 px-4 sm:px-6 overflow-hidden" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #edf6fa 15%, #d4ebf5 40%, #c0ddef 60%, #b0d5e6 100%)' }} data-testid="ophthalmologist-section">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-[#045474] text-3xl sm:text-4xl lg:text-5xl font-light mb-6" style={{ fontFamily: "'Barlow', sans-serif" }}>
              {ctaCards.title || 'Vous êtes Ophtalmologiste ?'}
            </h2>
            <button onClick={() => navigate('/adhesion')} className="inline-flex items-center gap-2 px-8 py-3 bg-[#35AFE4] text-white rounded-full font-semibold text-sm hover:bg-[#1e9bd4] transition-all" data-testid="join-btn">
              <span className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold">+</span>
              {ctaCards.cta_text || 'Rejoignez-nous !'}
            </button>
          </div>

          {/* 3 CTA cards — titles navy #045474 + light gray buttons like reference */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-16">
            <div className="bg-white/80 rounded-2xl p-8 sm:p-10 text-left cursor-pointer group hover:shadow-lg transition-all shadow-sm" onClick={() => navigate('/annuaire')} data-testid="cta-card-0">
              <h3 className="text-[#045474] text-2xl font-bold mb-4 leading-tight" style={{ fontFamily: "'Barlow', sans-serif" }}>Référencement Sur L'annuaire</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">Vous pouvez référencer vos différents lieux de consultation, avec les spécificités de chacun</p>
              <span className="inline-block px-6 py-2.5 bg-[#f9f9f9] border-2 border-[#045474] text-[#045474] rounded-lg text-sm font-medium group-hover:bg-[#045474] group-hover:text-white transition-all">M'enregistrer</span>
            </div>
            <div className="bg-white/80 rounded-2xl p-8 sm:p-10 text-left cursor-pointer group hover:shadow-lg transition-all shadow-sm" onClick={() => navigate('/forum')} data-testid="cta-card-1">
              <h3 className="text-[#045474] text-2xl font-bold mb-4 leading-tight" style={{ fontFamily: "'Barlow', sans-serif" }}>Accès Au Forum D'entraide</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">En tant que membre, accédez à un forum privé, afin d'échanger sur des sujets entre praticiens</p>
              <span className="inline-block px-6 py-2.5 bg-[#f9f9f9] border-2 border-[#045474] text-[#045474] rounded-lg text-sm font-medium group-hover:bg-[#045474] group-hover:text-white transition-all">Accéder au forum</span>
            </div>
            <div className="bg-white/80 rounded-2xl p-8 sm:p-10 text-left cursor-pointer group hover:shadow-lg transition-all shadow-sm" onClick={() => navigate('/nos-formations')} data-testid="cta-card-2">
              <h3 className="text-[#045474] text-2xl font-bold mb-4 leading-tight" style={{ fontFamily: "'Barlow', sans-serif" }}>Accès Aux Formations</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">En tant que membre, accédez à nos articles de formation, aux replays & aux cours</p>
              <span className="inline-block px-6 py-2.5 bg-[#f9f9f9] border-2 border-[#045474] text-[#045474] rounded-lg text-sm font-medium group-hover:bg-[#045474] group-hover:text-white transition-all">Accéder aux cours</span>
            </div>
          </div>

          {/* "et accédez à un contenu exclusif" */}
          <div className="text-center mb-10">
            <h3 className="text-[#045474] text-2xl sm:text-3xl font-light" style={{ fontFamily: "'Barlow', sans-serif" }}>
              et accédez à un contenu <span className="text-[#35AFE4] font-semibold italic">exclusif</span>
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { title: 'Les publications scientifiques', route: '/nos-formations?cat=Les Publications', icon: FileText },
              { title: 'Les replays', route: '/nos-formations?cat=Les Replays', icon: Play },
              { title: 'Les rapports SFOALC', route: '/nos-formations?cat=Les Rapports', icon: Award },
            ].map((card, i) => (
              <div key={i} className="group bg-white/70 rounded-2xl p-8 sm:p-10 text-center cursor-pointer hover:shadow-lg transition-all shadow-sm border border-white/50" onClick={() => navigate(card.route)} data-testid={`exclusive-card-${i}`}>
                <card.icon size={40} className="text-[#35AFE4] mx-auto mb-5" strokeWidth={1.2} />
                <h3 className="text-lg font-bold mb-5 text-[#045474]" style={{ fontFamily: "'Barlow', sans-serif" }}>{card.title}</h3>
                <span className="inline-block px-6 py-2 bg-[#f9f9f9] border-2 border-[#045474] text-[#045474] rounded-lg text-sm font-medium group-hover:bg-[#045474] group-hover:text-white transition-all">Découvrir</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PARTENAIRES — blue eye bg (actu.png) with curve ===== */}
      <section ref={partnersRef} className="relative py-20 sm:py-28 px-4 sm:px-6 overflow-hidden" data-testid="partners-section">
        {/* Background = close-up blue eye */}
        <div className="absolute inset-0">
          <img src="/actu.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#2a5f7f]/60" />
        </div>
        {/* Top curve — light blue to match the section above */}
        <div className="absolute top-0 left-0 right-0 z-10" style={{ transform: 'translateY(-1px)' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '60px' }}>
            <path fill="#b0d5e6" d="M1000,4.3V0H0v4.3C0.9,23.1,126.7,99.2,500,100S1000,22.7,1000,4.3z" />
          </svg>
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <h2 className="text-white text-2xl sm:text-3xl font-bold mb-14" style={{ fontFamily: "'Barlow', sans-serif" }}>
            {partners.title || 'Nos partenaires'}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-10 sm:gap-16 items-center">
            {(partners.logos || ['eclso', 'ldc', 'sfo', 'snof']).map((p) => (
              <div key={p} className="flex items-center justify-center p-6 group">
                <img src={`/partner-${p}.png`} alt={p.toUpperCase()} className="max-h-14 sm:max-h-20 w-auto object-contain opacity-90 group-hover:opacity-100 transition-all duration-500 group-hover:scale-110 drop-shadow-[0_0_8px_rgba(255,255,255,0.15)]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
